// Browser-based geolocation tracking service for mobile web users.
//
// On the Capacitor native shell (.apk), this same service swaps in
// `@capacitor-community/background-geolocation` so tracking keeps
// running while the screen is locked / the user is in another app.
// Web users continue to use `navigator.geolocation.watchPosition`.

let trackingInterval = null;
let watchId = null;
let nativeWatcherId = null;        // capgo background-geolocation watcher id
let isNativeShell = false;         // detected lazily

const TRACKING_INTERVAL = 5 * 60 * 1000; // 5 minutes
import { BACKEND_URL } from '../utils/backendUrl';
import { isNativeApp } from '../native/capacitorBootstrap';
const API_URL = BACKEND_URL;

class LocationTrackingService {
  constructor() {
    this.isTracking = false;
    this.lastPosition = null;
  }

  // Start automatic location tracking
  async startTracking() {
    isNativeShell = isNativeApp();
    if (isNativeShell) {
      return this.startNativeTracking();
    }
    if (!navigator.geolocation) {
      return { success: false, message: 'Geolocation not supported' };
    }

    try {
      // Request permission and get initial position
      const position = await this.getCurrentPosition();
      
      // Send initial location
      await this.sendLocation(position);
      
      // Start periodic tracking
      this.isTracking = true;
      
      // Use watchPosition for real-time updates (more battery efficient than interval)
      watchId = navigator.geolocation.watchPosition(
        (position) => this.handlePositionUpdate(position),
        (error) => this.handlePositionError(error),
        {
          enableHighAccuracy: false, // Battery-friendly
          timeout: 30000,
          maximumAge: TRACKING_INTERVAL
        }
      );

      // Also use interval as backup
      trackingInterval = setInterval(() => {
        this.updateLocation();
      }, TRACKING_INTERVAL);

      return { success: true };
    } catch (error) {
      // Permission denied or geolocation unavailable is a normal browser state, not an error
      console.warn('Location tracking unavailable:', error?.message || error);
      return { success: false, message: error?.message || 'Geolocation unavailable' };
    }
  }

  // ─────────────────────────────────────────────────────────
  // Native (Capacitor .apk) tracking — keeps reporting even
  // when the screen is locked thanks to the plugin's Android
  // foreground service.
  // ─────────────────────────────────────────────────────────
  async startNativeTracking() {
    try {
      const { BackgroundGeolocation } = await import('@capacitor-community/background-geolocation');

      // Add a watcher. The plugin returns a watcher id we hold onto so
      // stopTracking() can remove it cleanly. requestPermissions=true
      // triggers the OS prompt the first time.
      nativeWatcherId = await BackgroundGeolocation.addWatcher(
        {
          // Title + body of the persistent notification that Android
          // shows while the foreground service is alive. Required on
          // API 26+ for background location.
          backgroundMessage: 'Tracking visits in the background',
          backgroundTitle: 'Zartec CRM',
          requestPermissions: true,
          stale: false,
          // Throttle in metres before reporting a new position. Matches
          // the web side's 50 m filter.
          distanceFilter: 50,
        },
        async (location, error) => {
          if (error) {
            // NOT_AUTHORIZED → user denied. Don't spam errors.
            if (error.code === 'NOT_AUTHORIZED') {
              this.isTracking = false;
            }
            return;
          }
          if (!location) return;
          // Mirror the browser-side payload so the backend doesn't
          // need to know which platform sent it.
          await this.sendLocation({
            coords: {
              latitude: location.latitude,
              longitude: location.longitude,
              accuracy: location.accuracy,
            },
            timestamp: location.time || Date.now(),
          });
        }
      );
      this.isTracking = true;
      return { success: true, native: true };
    } catch (e) {
      console.warn('[locationTracking] native start failed:', e?.message || e);
      return { success: false, message: e?.message || 'Native tracking failed' };
    }
  }

  // Get current position as promise
  getCurrentPosition() {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 60000
      });
    });
  }

  // Handle position updates from watchPosition
  async handlePositionUpdate(position) {
    // Only send if position changed significantly (>50 meters)
    if (this.lastPosition) {
      const distance = this.calculateDistance(
        this.lastPosition.coords.latitude,
        this.lastPosition.coords.longitude,
        position.coords.latitude,
        position.coords.longitude
      );
      
      if (distance < 0.05) { // Less than 50 meters
        return; // Don't send duplicate positions
      }
    }

    this.lastPosition = position;
    await this.sendLocation(position);
  }

  // Handle position errors
  handlePositionError(error) {
    // Don't alert user - silent tracking
  }

  // Calculate distance between two coordinates (in km)
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) *
        Math.cos(this.toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toRad(degrees) {
    return degrees * (Math.PI / 180);
  }

  // Update location manually
  async updateLocation() {
    try {
      const position = await this.getCurrentPosition();
      await this.sendLocation(position);
    } catch (error) {
      console.error(error);
    }
  }

  // Send location to backend (auth via httpOnly cookie on same-origin)
  async sendLocation(position) {
    try {
      const response = await fetch(`${API_URL}/api/locations`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date(position.timestamp).toISOString()
        })
      });

      if (!response.ok) {
        // Restricted role (e.g. employee) — degrade silently
        return;
      }
    } catch (error) {
      // Network error — buffer for later sync
      this.storeOfflineLocation(position);
    }
  }

  // Store location offline for later sync
  storeOfflineLocation(position) {
    const offlineLocations = JSON.parse(localStorage.getItem('offline_locations') || '[]');
    offlineLocations.push({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: new Date(position.timestamp).toISOString()
    });
    localStorage.setItem('offline_locations', JSON.stringify(offlineLocations));
  }

  // Sync offline locations when back online (auth via httpOnly cookie)
  async syncOfflineLocations() {
    const offlineLocations = JSON.parse(localStorage.getItem('offline_locations') || '[]');
    
    if (offlineLocations.length === 0) return;

    try {
      for (const location of offlineLocations) {
        await fetch(`${API_URL}/api/locations`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(location)
        });
      }
      
      // Clear synced locations
      localStorage.removeItem('offline_locations');
    } catch (error) {
      console.error('Failed to sync offline locations:', error);
    }
  }

  // Stop tracking
  async stopTracking() {
    if (nativeWatcherId) {
      try {
        const { BackgroundGeolocation } = await import('@capacitor-community/background-geolocation');
        await BackgroundGeolocation.removeWatcher({ id: nativeWatcherId });
      } catch {
        /* swallow — best-effort cleanup */
      }
      nativeWatcherId = null;
    }
    if (trackingInterval) {
      clearInterval(trackingInterval);
      trackingInterval = null;
    }

    if (watchId) {
      navigator.geolocation.clearWatch(watchId);
      watchId = null;
    }

    this.isTracking = false;
  }
}

export default new LocationTrackingService();
